import { Link } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import client from "@/lib/api";
import { toast } from "sonner";

export default function Footer() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      const r = await client.post("/newsletter/subscribe", { email, source: "footer" });
      toast.success(r.data?.already_subscribed
        ? "Sie sind bereits eingetragen: vielen Dank!"
        : "Fast geschafft: Bitte bestätigen Sie Ihre Anmeldung über den Link in der E-Mail, die wir Ihnen gerade geschickt haben.");
      setEmail("");
    } catch (er) {
      toast.error("Bitte gültige E-Mail eingeben.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="border-t border-cream/[0.06] bg-ink-950 pt-24 pb-10 mt-24" data-testid="site-footer">
      <div className="container-x grid gap-14 md:grid-cols-12">
        <div className="md:col-span-4">
          <div className="font-display text-2xl tracking-tight mb-6 text-cream">
            Picturebox<span className="text-gold">·</span>24
          </div>
          <p className="text-cream/60 text-sm leading-[1.9] max-w-sm mb-6">
            {t("footer.tagline")}
          </p>
          <form onSubmit={subscribe} className="max-w-sm" data-testid="newsletter-form">
            <p className="eyebrow mb-3"><span className="gold-line" /> Newsletter</p>
            <div className="flex">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ihre@email.de"
                data-testid="newsletter-email"
                className="flex-1 bg-ink-900/60 border border-cream/15 focus:border-gold text-cream px-3 py-2 text-sm outline-none"
              />
              <button
                type="submit"
                disabled={submitting}
                data-testid="newsletter-submit"
                className="border border-l-0 border-gold text-gold hover:bg-gold hover:text-ink-950 px-4 uppercase tracking-[0.22em] text-xs transition-colors disabled:opacity-50"
              >
                {submitting ? "…" : "OK"}
              </button>
            </div>
            <p className="text-[0.62rem] text-cream/70 mt-2">Angebote &amp; Neuheiten per E-Mail. Sie bekommen erst eine Bestätigungsmail, jederzeit abbestellbar. Details in der <a href="/datenschutz" className="underline">Datenschutzerklärung</a>.</p>
          </form>
        </div>

        <div className="md:col-span-2">
          <p className="eyebrow mb-5">{t("footer.explore")}</p>
          <ul className="space-y-3 text-cream/70 text-sm">
            <li><Link to="/privatkunden" className="link-underline">{t("nav.private")}</Link></li>
            <li><Link to="/businesskunden" className="link-underline">{t("nav.business")}</Link></li>
            <li><Link to="/produkte" className="link-underline">{t("nav.products")}</Link></li>
            <li><Link to="/layouts" className="link-underline">{t("nav.layouts")}</Link></li>
            <li><Link to="/ablauf" className="link-underline">{t("nav.process")}</Link></li>
          </ul>
        </div>

        <div className="md:col-span-3">
          <p className="eyebrow mb-5">{t("footer.service")}</p>
          <ul className="space-y-3 text-cream/70 text-sm">
            {/* Session 12.6: Referenzen-Link entfernt */}
            <li><Link to="/faq" className="link-underline">FAQ</Link></li>
            <li><Link to="/kontakt" className="link-underline">{t("nav.contact")}</Link></li>
            <li><Link to="/admin/login" className="link-underline text-cream/70">Admin</Link></li>
            <li><Link to="/interne-buchung" className="link-underline text-cream/70" data-testid="footer-pos">Interne Buchung</Link></li>
          </ul>
        </div>

        <div className="md:col-span-3">
          <p className="eyebrow mb-5">{t("footer.contact_us")}</p>
          <ul className="space-y-2 text-cream/70 text-sm leading-relaxed">
            <li>Picturebox24: Inh. Kevin Kreul</li>
            <li>Im Fuhlenbrock 113</li>
            <li>46242 Bottrop</li>
            <li className="pt-4">
              <a href="mailto:info@picturebox24.de" className="link-underline text-gold">
                info@picturebox24.de
              </a>
            </li>
            <li>
              <a href="tel:02041 7355679" className="link-underline text-cream/70">
                02041 / 7355679
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="container-x mt-20">
        <div className="hairline" />
        <div className="pt-6 flex flex-col md:flex-row gap-4 justify-between text-xs text-cream/70 uppercase tracking-[0.25em]">
          <span>© {new Date().getFullYear()} Picturebox24: Inh. Kevin Kreul · {t("footer.rights")}</span>
          <span className="flex flex-wrap gap-x-6 gap-y-2 justify-start md:justify-end">
            <Link to="/impressum" className="link-underline" data-testid="footer-impressum">{t("footer.imprint")}</Link>
            <Link to="/datenschutz" className="link-underline" data-testid="footer-datenschutz">{t("footer.privacy")}</Link>
            <Link to="/agb" className="link-underline" data-testid="footer-agb">{t("footer.terms")}</Link>
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent("pb24-open-consent"))}
              className="link-underline"
              data-testid="footer-cookie-settings"
            >
              Cookie-Einstellungen
            </button>
          </span>
        </div>
      </div>
    </footer>
  );
}
